import { useCallback, useEffect, useMemo, useRef } from "react";

const STALL_HINT_MS = 3000;
const STALL_ESCALATE_MS = 10000;
const STALL_POLL_INTERVAL_MS = 1000;

const STALL_MESSAGES = {
  stillWorking: "Still working...",
  stillWorkingEscalated: "Still working — preparing the rest of the answer...",
  preparingCitations: "Preparing links and citations...",
};

/**
 * Detects gaps between SSE stream events and surfaces a progress banner so users
 * know Ask Addy is still working (not stuck or finished).
 *
 * @param {{ setAgentStatus: (status: { type: string, message: string }) => void }} options
 */
export function useStreamStallWatchdog({ setAgentStatus }) {
  const lastActivityTsRef = useRef(Date.now());
  const intervalIdRef = useRef(null);
  const lastEventKindRef = useRef("chunk");
  const isRunningRef = useRef(false);
  const setAgentStatusRef = useRef(setAgentStatus);

  useEffect(() => {
    setAgentStatusRef.current = setAgentStatus;
  }, [setAgentStatus]);

  const resolveStallMessage = useCallback((idleMs) => {
    const kind = lastEventKindRef.current;

    if (kind === "tool") {
      return null;
    }

    if (kind === "citations") {
      return STALL_MESSAGES.preparingCitations;
    }

    if (idleMs >= STALL_ESCALATE_MS) {
      return STALL_MESSAGES.stillWorkingEscalated;
    }

    if (idleMs >= STALL_HINT_MS) {
      return STALL_MESSAGES.stillWorking;
    }

    return null;
  }, []);

  const poll = useCallback(() => {
    if (!isRunningRef.current) {
      return;
    }

    const idleMs = Date.now() - lastActivityTsRef.current;
    const message = resolveStallMessage(idleMs);

    if (message) {
      setAgentStatusRef.current({ type: "progress", message });
    }
  }, [resolveStallMessage]);

  const clearIntervalIfNeeded = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    isRunningRef.current = false;
    clearIntervalIfNeeded();
    setAgentStatusRef.current({ type: "", message: "" });
  }, [clearIntervalIfNeeded]);

  const start = useCallback(() => {
    clearIntervalIfNeeded();
    isRunningRef.current = true;
    lastActivityTsRef.current = Date.now();
    lastEventKindRef.current = "chunk";
    intervalIdRef.current = setInterval(poll, STALL_POLL_INTERVAL_MS);
  }, [clearIntervalIfNeeded, poll]);

  const trackActivity = useCallback((kind = "chunk") => {
    lastActivityTsRef.current = Date.now();
    if (kind) {
      lastEventKindRef.current = kind;
    }
  }, []);

  useEffect(() => {
    return () => {
      isRunningRef.current = false;
      clearIntervalIfNeeded();
    };
  }, [clearIntervalIfNeeded]);

  return useMemo(
    () => ({ start, trackActivity, stop }),
    [start, trackActivity, stop]
  );
}

/**
 * Maps a stream event from Chat.js to a watchdog activity kind.
 * @param {unknown} response
 * @returns {"chunk"|"tool"|"citations"|"other"}
 */
export function getStreamEventKind(response) {
  if (typeof response === "string") {
    return "chunk";
  }

  if (!response || typeof response !== "object") {
    return "other";
  }

  switch (response.type) {
    case "progress":
      return "tool";
    case "insight":
      return "other";
    default:
      return "other";
  }
}
